import { useRef, useState, useCallback, useEffect } from "react";
import { createDecartClient, models } from "@decartai/sdk";
import { fetchRealtimeToken } from "@/lib/decartApi";
import { toast } from "sonner";
import { logDebug, logWarn, logError } from "@/lib/log";

const WATCHDOG_MS = 35000;

// Map internal/provider error strings to messages that make sense to streamers.
// "Insufficient credits" from the Decart platform means OUR provider account balance,
// not the user's SPCLIVE credits — showing it raw sends users to top up in vain.
function friendlyStartError(message) {
    const msg = message || "Failed to start session";
    if (/insufficient credits/i.test(msg)) {
        return "Streaming engine capacity is temporarily unavailable — please try again shortly. If this persists, contact support.";
    }
    if (/stale connect attempt/i.test(msg)) {
        return "Could not start the stream — please try again.";
    }
    return msg;
}

function safePlay(el) {
    try {
        const p = el?.play?.();
        p?.catch?.(() => {});
    } catch (e) {
        /* jsdom / autoplay-blocked: ignore */
    }
}

// Decart's frame-timing trailer pipeline (LiveKit frame-metadata worker) intermittently
// yields undecodable (black) remote video. Hiding these APIs during connect makes the
// SDK negotiate plain frames — only an internal latency metric is lost.
function hideFrameMetadataSupport() {
    const targets = [
        [window.RTCRtpSender?.prototype, "createEncodedStreams"],
        [window.RTCRtpReceiver?.prototype, "createEncodedStreams"],
        [window, "RTCRtpScriptTransform"],
    ];
    const restores = [];
    targets.forEach(([obj, key]) => {
        if (!obj || !(key in obj)) return;
        const desc = Object.getOwnPropertyDescriptor(obj, key);
        if (!desc || !desc.configurable) return;
        try {
            delete obj[key];
            restores.push(() => Object.defineProperty(obj, key, desc));
        } catch (e) {
            /* leave as-is */
        }
    });
    return () => restores.forEach((r) => r());
}

async function acquireCameraStream(model, orientation) {
    const portrait = orientation === "portrait";
    const fps = typeof model.fps === "number" ? model.fps : model.fps?.ideal || 30;
    try {
        return await navigator.mediaDevices.getUserMedia({
            video: {
                frameRate: { ideal: fps },
                width: { ideal: portrait ? model.height : model.width },
                height: { ideal: portrait ? model.width : model.height },
            },
            audio: false,
        });
    } catch (err) {
        throw new Error(
            `Camera access denied — please allow webcam permission. (${err.message || err.name})`
        );
    }
}

function connectWithWatchdog(decartClient, stream, connectOptions, watchdogRef) {
    let timedOut = false;
    const watchdog = new Promise((_, reject) => {
        watchdogRef.current = setTimeout(() => {
            timedOut = true;
            reject(
                new Error(
                    "Connection timeout (35s). Your network may be blocking WebRTC (UDP). Try Chrome/Firefox on a different network."
                )
            );
        }, WATCHDOG_MS);
    });
    const connectPromise = decartClient.realtime.connect(stream, connectOptions);
    return { connectPromise, watchdog, isTimedOut: () => timedOut };
}

function disposeLateClient(connectPromise, realtimeClientRef, mediaStreamRef, isTimedOut) {
    // Dispose a client that resolves after timeout/unmount so no orphan connection leaks
    connectPromise
        .then((client) => {
            const isCurrent = client === realtimeClientRef.current;
            if (!isCurrent && (isTimedOut() || !mediaStreamRef.current)) {
                try {
                    if (typeof client.disconnect === "function") client.disconnect();
                } catch (e) {
                    logWarn("[SPCLIVE] late-connect dispose failed", e);
                }
            }
        })
        .catch(() => {});
}

function registerClientListeners(realtimeClient, { updateState, setStatusDetail, setErrorMessage, cleanup }) {
    realtimeClient.on("connectionChange", (state) => {
        logDebug("[SPCLIVE] connectionChange:", state);
        if (state === "connected" || state === "generating") {
            updateState("live");
            setStatusDetail(state === "generating" ? "Streaming frames" : "Connected");
        } else if (state === "reconnecting") {
            updateState("reconnecting");
            setStatusDetail("Reconnecting...");
        } else if (state === "disconnected") {
            // Session ended on its own (max duration, network drop): release camera + client
            cleanup();
            updateState("idle");
            toast.info("Session ended — camera released");
        }
    });

    realtimeClient.on("error", (sdkError) => {
        logError("[SPCLIVE] SDK error:", sdkError);
        const msg = friendlyStartError(sdkError?.message || "Realtime stream error");
        setErrorMessage(msg);
        cleanup();
        updateState("error");
        toast.error(msg);
    });
}

async function connectRealtime({
    decartClient,
    model,
    stream,
    prompt,
    referenceFile,
    speed,
    onRemoteStream,
    watchdogRef,
    realtimeClientRef,
    mediaStreamRef,
}) {
    const initialState = { prompt: { text: prompt, enhance: true } };
    if (referenceFile) initialState.image = referenceFile;
    const connectOptions = { model, mirror: "auto", onRemoteStream, initialState, preferredVideoCodec: "vp8" };
    if (speed === "fast") connectOptions.speed = "fast";
    const restoreFrameMetadata = hideFrameMetadataSupport();
    try {
        const { connectPromise, watchdog, isTimedOut } = connectWithWatchdog(
            decartClient,
            stream,
            connectOptions,
            watchdogRef
        );
        disposeLateClient(connectPromise, realtimeClientRef, mediaStreamRef, isTimedOut);
        const realtimeClient = await Promise.race([connectPromise, watchdog]);
        if (watchdogRef.current) {
            clearTimeout(watchdogRef.current);
            watchdogRef.current = null;
        }
        return realtimeClient;
    } finally {
        restoreFrameMetadata();
    }
}

async function runStartSequence({
    prompt,
    referenceFile,
    orientation,
    speed,
    setStatusDetail,
    setHasOutput,
    localVideoRef,
    remoteVideoRef,
    mediaStreamRef,
    realtimeClientRef,
    watchdogRef,
}) {
    const { apiKey } = await fetchRealtimeToken();
    setStatusDetail("Acquiring camera...");

    const decartClient = createDecartClient({ apiKey });
    const model = models.realtime("lucy-2.5");

    const stream = await acquireCameraStream(model, orientation);
    mediaStreamRef.current = stream;
    if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
        safePlay(localVideoRef.current);
    }
    setStatusDetail("Negotiating WebRTC with Decart...");

    return connectRealtime({
        decartClient,
        model,
        stream,
        prompt,
        referenceFile,
        speed,
        onRemoteStream: (remoteStream) => {
            logDebug(
                "[SPCLIVE] remote stream received:",
                remoteStream?.getVideoTracks?.().length, "video /",
                remoteStream?.getAudioTracks?.().length, "audio tracks"
            );
            if (remoteVideoRef.current) {
                remoteVideoRef.current.srcObject = remoteStream;
                safePlay(remoteVideoRef.current);
                setHasOutput(true);
            }
        },
        watchdogRef,
        realtimeClientRef,
        mediaStreamRef,
    });
}

async function applyPromptToClient(rc, text, image) {
    const payload = {};
    if (text && text.trim().length > 0) {
        payload.prompt = text;
        payload.enhance = true;
    }
    if (image !== undefined) {
        payload.image = image; // File, Blob, URL string, or null to clear
    }
    if (Object.keys(payload).length === 0) return;
    if (typeof rc.set === "function") {
        await rc.set(payload);
    } else if (typeof rc.setPrompt === "function" && payload.prompt) {
        await rc.setPrompt(payload.prompt, { enhance: true });
    }
}

export function useDecartSession(onConnectionStateChange) {
    const localVideoRef = useRef(null);
    const remoteVideoRef = useRef(null);
    const realtimeClientRef = useRef(null);
    const mediaStreamRef = useRef(null);
    const watchdogRef = useRef(null);
    const stateRef = useRef("idle");

    const [connectionState, setConnectionState] = useState("idle"); // idle | connecting | live | reconnecting | error
    const [errorMessage, setErrorMessage] = useState(null);
    const [hasOutput, setHasOutput] = useState(false);
    const [statusDetail, setStatusDetail] = useState("");

    const updateState = useCallback(
        (s) => {
            stateRef.current = s;
            setConnectionState(s);
            onConnectionStateChange?.(s);
        },
        [onConnectionStateChange]
    );

    const cleanup = useCallback(() => {
        if (watchdogRef.current) {
            clearTimeout(watchdogRef.current);
            watchdogRef.current = null;
        }
        try {
            const rc = realtimeClientRef.current;
            if (rc && typeof rc.disconnect === "function") rc.disconnect();
        } catch (e) {
            logWarn("disconnect error", e);
        }
        realtimeClientRef.current = null;

        const stream = mediaStreamRef.current;
        if (stream) {
            stream.getTracks().forEach((t) => t.stop());
            mediaStreamRef.current = null;
        }
        if (localVideoRef.current) localVideoRef.current.srcObject = null;
        if (remoteVideoRef.current) remoteVideoRef.current.srcObject = null;
        setHasOutput(false);
        setStatusDetail("");
    }, []);

    useEffect(() => () => cleanup(), [cleanup]);

    const startSession = useCallback(
        async ({ prompt, referenceFile, orientation, speed }) => {
            if (stateRef.current === "connecting" || stateRef.current === "live") return;

            setErrorMessage(null);
            updateState("connecting");
            setStatusDetail("Minting token...");

            try {
                const realtimeClient = await runStartSequence({
                    prompt,
                    referenceFile,
                    orientation,
                    speed,
                    setStatusDetail,
                    setHasOutput,
                    localVideoRef,
                    remoteVideoRef,
                    mediaStreamRef,
                    realtimeClientRef,
                    watchdogRef,
                });

                realtimeClientRef.current = realtimeClient;
                registerClientListeners(realtimeClient, {
                    updateState,
                    setStatusDetail,
                    setErrorMessage,
                    cleanup,
                });

                updateState("live");
                setStatusDetail("Waiting for first frames...");
                toast.success("LIVE — transformation engine engaged");
            } catch (err) {
                logError("[SPCLIVE] Failed to start session:", err);
                const msg = friendlyStartError(err?.message);
                setErrorMessage(msg);
                updateState("error");
                toast.error(msg);
                cleanup();
            }
        },
        [updateState, cleanup]
    );

    const stopSession = useCallback(() => {
        cleanup();
        updateState("idle");
        toast.info("Session ended");
    }, [cleanup, updateState]);

    const applyPrompt = useCallback(async (text, image) => {
        const rc = realtimeClientRef.current;
        if (!rc) return;
        try {
            await applyPromptToClient(rc, text, image);
        } catch (e) {
            logWarn("[SPCLIVE] Failed to update prompt:", e);
        }
    }, []);

    const isConnected = useCallback(
        () => Boolean(realtimeClientRef.current?.isConnected?.()),
        []
    );

    return {
        localVideoRef,
        remoteVideoRef,
        connectionState,
        errorMessage,
        hasOutput,
        statusDetail,
        startSession,
        stopSession,
        applyPrompt,
        isConnected,
    };
}
